import { useFeatureFlagsStore } from '@/stores/featureFlags';
import { usePlanAccessStore } from '@/stores/planAccess';

/**
 * BS-004 — the single place the interface asks "is this feature switched on?".
 *
 * The answer has two independent sources and they are NOT the same question:
 *
 *   featureFlags.hidden      the RELEASE flag. bsapi reads HIDDEN_FEATURES from
 *                            its own .env, so a module can be pulled from
 *                            production while staging keeps it.
 *   featureFlags.userHidden  the ACCOUNT entitlement (users.feature_smart_quote
 *                            / feature_mat_ai), fed in by planAccess.load() via
 *                            applyUserFlags().
 *
 * `isHidden` already ORs the two, which gives us the precedence the ticket asks
 * for — a module pulled from this environment cannot be switched back on by a
 * tenant toggle. The value of routing every caller through here is that the
 * rule stays in one place: before this, components asked the question four
 * different ways (isHidden, planAccess.isAllowed, a raw feature_smart_quote
 * read, and a `featureKey` property that two of the three menu components
 * silently ignored), so the answers disagreed.
 *
 * Do not confuse this with planAccess.isAllowed(). That is a PLAN-TIER check —
 * "does this subscription include the feature" — and it renders a lock that
 * sends the user to the upgrade page. This is an entitlement check: the feature
 * is not merely unpaid for, it is not there, and the item should not render at
 * all. `requiresPlanFeature` drives the former, `featureKey` the latter; they
 * happen to share the string 'quotes' and mean different things by it.
 *
 * Nothing here keeps its own copy of the flags. This is a thin façade on
 * purpose: the moment it grows its own state it becomes the fifth answer.
 */

/**
 * Keys that fail closed until the account entitlement (planAccess) has loaded.
 * Quotes is invite-only (users.feature_smart_quote, off until Built Simple
 * staff switch it on). Mat AI is invite-only too, but its surfaces were not
 * part of #512 and keep their own checks for now.
 */
const FAIL_CLOSED_FEATURES = new Set(['quotes']);

/**
 * Is `key` switched off, either for this environment or for this account?
 *
 * FAILS CLOSED for Quotes on the entitlement (#512). Until planAccess has
 * loaded, userHidden is empty and `isHidden('quotes')` says "not hidden" for
 * every account. The router guard stops waiting after 5s, so "not loaded" has
 * to read as hidden here, or a slow planAccess shows Quotes to an account that
 * was never invited. A failed planAccess fetch already hides Quotes
 * (applyUserFlags(null)), so "not loaded" here means pending, or the guard
 * gave up waiting.
 *
 * The release flag (featureFlags.hidden) keeps its documented fail-open rule:
 * a failed /feature-flags fetch must not pull Quotes from invited accounts.
 *
 * This is the one gate for Quotes: the router guard (list and detail routes),
 * both menus, the featureAccess mixin and the estimate surfaces all ask here.
 */
export function isFeatureHidden(key: string): boolean {
    if (FAIL_CLOSED_FEATURES.has(key) && !usePlanAccessStore().isLoaded) return true;
    return useFeatureFlagsStore().isHidden(key);
}

/** The inverse, for the common `v-if="isFeatureEnabled('quotes')"` case. */
export function isFeatureEnabled(key: string): boolean {
    return !isFeatureHidden(key);
}

/**
 * Drop menu entries whose `featureKey` is hidden, recursing into `children`.
 *
 * Every nav component declared `featureKey` on its items; only
 * HorizontalHeader.vue actually read it. That is the reported bug: the vertical
 * sidebar rendered "Quotes" for an account with Smart Quote switched off,
 * leading to an area that does nothing.
 *
 * A parent whose children all disappear is dropped with them — an empty
 * dropdown is the same dead end in a different shape. A parent with no children
 * to begin with (a plain link) is left alone.
 */
export function filterMenuByFeature<T extends { featureKey?: string; children?: any[] }>(items: T[]): T[] {
    if (!Array.isArray(items)) return [];

    return items.reduce<T[]>((kept, item) => {
        if (!item) return kept;
        if (item.featureKey && isFeatureHidden(item.featureKey)) return kept;

        if (Array.isArray(item.children) && item.children.length > 0) {
            const children = filterMenuByFeature(item.children);
            if (children.length === 0) return kept;
            kept.push({ ...item, children });
            return kept;
        }

        kept.push(item);
        return kept;
    }, []);
}
